import { Component, OnInit, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { groupBy, sortBy, type Dictionary } from 'lodash';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { BranchService } from '../../../service/branch.service';
import { EdgeDeviceService } from '../../../service/edge-device.service';
import { NotifyService } from '../../../service/notify.service';
import { Branch, BranchCameraWithCameras, BranchDetailsResponse, KioskProvider, LocationCamera } from '../../../types';
import { EdgeDevice } from '../../../types/edge-device';
import { InstallerDashboardTablePanobridgeComponent } from './installer-dashboard-table-panobridge/installer-dashboard-table-panobridge.component';
import { InstallerDashboardTableShoppersenseComponent } from './installer-dashboard-table-shoppersense/installer-dashboard-table-shoppersense.component';

@Component({
  selector: 'app-installer-dashboard',
  templateUrl: './installer-dashboard.component.html',
  styleUrls: ['./installer-dashboard.component.scss'],
  standalone: false,
})
export class InstallerDashboardComponent implements OnInit {
  private edgeDeviceService = inject(EdgeDeviceService);
  private branchService = inject(BranchService);
  private notifyService = inject(NotifyService);

  private queryParams = injectQueryParams();

  public branchId = input.required<number>();
  public branchData = model.required<Branch>();

  public isLoading = signal<boolean>(false);

  public setupDeviceModal = viewChild<PanModalComponent>('setupDevice');
  public shoppersenseComponent = viewChild<InstallerDashboardTableShoppersenseComponent>(InstallerDashboardTableShoppersenseComponent);
  public panobridgeComponent = viewChild<InstallerDashboardTablePanobridgeComponent>(InstallerDashboardTablePanobridgeComponent);

  public deviceId = computed(() => this.queryParams().device_id);
  public deviceSerialNumber = computed(() => this.queryParams().device_sn);

  public cameraProviders = signal<BranchCameraWithCameras[]>([]);
  public allCameraProviders = signal<BranchCameraWithCameras[]>([]);

  public allBranchAliases = signal<BranchDetailsResponse['kiosk_providers']>([]);
  public allKioskProviders = signal<KioskProvider[]>([]);

  public branchCamerasByLocationId = signal<Dictionary<LocationCamera[]>>({});
  public branchLocations = signal<BranchDetailsResponse['locations']>([]);

  private allEdgeDevices = signal<EdgeDevice[]>([]);

  public allShoppersenses = computed(() => this.allEdgeDevices().filter((d) => this.edgeDeviceService.isShopperSense(d)));
  public allPanobridges = computed(() => this.allEdgeDevices().filter((d) => this.edgeDeviceService.isPanoBridge(d)));

  public fixableErrors = signal<Array<{ subject: string; error: string }>>([]);

  public editBranchExternalId = signal<string>('');

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    this.isLoading.set(true);
    this.branchCamerasByLocationId.set({});
    this.branchLocations.set([]);

    forkJoin({ providers: this.branchService.getProviders() }).subscribe(({ providers }) => {
      this.allCameraProviders.set(sortBy(providers.camera_providers, 'client_name'));
      this.allKioskProviders.set(sortBy(providers.kiosk_providers, 'alias'));

      this.checkIfDeviceRegistrationIsPossible();

      this.updateBranchData();
    });
  }

  private checkIfDeviceRegistrationIsPossible() {
    if (!this.deviceSerialNumber()) return;

    this.setupDeviceModal()?.show();
  }

  public tryRegisterShoppersense() {
    const comp = this.shoppersenseComponent();
    comp?.newShoppersenseSerialNumber.set(this.deviceSerialNumber());
    comp?.newShoppersenseModal()?.show();
  }

  public tryRegisterPanobridge() {
    const comp = this.panobridgeComponent();
    comp?.newPanobridgeSerialNumber.set(this.deviceSerialNumber());
    comp?.newPanobridgeModal()?.show();
  }

  updateBranchData() {
    if (!this.branchData()) return;

    const externalId = this.branchData().external_id;
    if (externalId) {
      this.editBranchExternalId.set(externalId);
    }

    this.isLoading.set(true);

    forkJoin({
      details: this.branchService.getBranchDetails(this.branchId()),
      edgeDevices: this.edgeDeviceService.getEdgeDevices([this.branchId()]),
    })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        ({ details, edgeDevices }) => {
          this.cameraProviders.set(details.camera_providers);
          this.branchLocations.set(details.locations);
          this.allBranchAliases.set(details.kiosk_providers);
          this.allEdgeDevices.set(edgeDevices.items);

          if (details.camera_errors) {
            this.extractErrors(details.camera_errors);
          }

          const allProviderCameras = details.camera_providers.flatMap((p) => p.cameras).filter((c) => !!c);
          const branchCameras = groupBy(allProviderCameras, (cam) => cam.location_id);
          this.branchCamerasByLocationId.set(branchCameras);
        },
        () => this.notifyService.notify(`Failed to load branch ${this.branchId()}`),
      );
  }

  saveBranchData() {
    this.branchService
      .updateBranch(this.branchId(), {
        name: this.branchData().name,
        external_id: this.editBranchExternalId(),
      })
      .subscribe({
        next: () => {
          this.branchData.update((b) => ({
            ...b,
            external_id: this.editBranchExternalId(),
          }));
          this.notifyService.notify(`Branch updated successfully`);
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  private extractErrors(cameraErrors: Record<string, string[]>) {
    const allErrors: { subject: string; error: string }[] = [];

    Object.keys(cameraErrors).forEach((subject) => {
      cameraErrors[subject].forEach((error) => {
        allErrors.push({
          subject,
          error,
        });
      });
    });

    this.fixableErrors.set(allErrors);
  }
}
